/* global CanvasRenderingContext2D, HTMLCanvasElement, Path2D, createImageBitmap */
/**
 * Draw a library card's picture (engine/assets/thumbScene's polygons) on a
 * canvas and hand it back as a small data URL.
 *
 * WebP when the browser makes it; a browser that hands back PNG instead
 * (WebKit) gets JPEG, which is smaller than that PNG. The result is held under
 * `max` characters - the schema's cap on a stored thumbnail - by stepping the
 * quality down; null when even that will not fit, or when there is no canvas
 * (jsdom), and the card then keeps its drawing of the kind.
 *
 * The colours are literals: a canvas reads no CSS variables. The ground is the
 * theme's `--display-ground`; a scene's people and a character's lines are its
 * `--accent`.
 */
import type { FigureLines, ThumbShot } from '../engine/assets/thumbScene';
import type { Viewport } from '../engine/battlefield/projection';

const GROUND = '#0a0806';
const FLOOR = '#1d1914';
const FLOOR_EDGE = 'rgba(255, 183, 90, 0.35)';
const ACCENT = '#c4a35a';
const QUALITIES = [0.8, 0.6, 0.4];

function trace(ctx: CanvasRenderingContext2D, points: readonly { x: number; y: number }[]): void {
  ctx.beginPath();
  points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
  ctx.closePath();
}

function paint(ctx: CanvasRenderingContext2D, shot: ThumbShot, view: Viewport): void {
  ctx.fillStyle = GROUND;
  ctx.fillRect(0, 0, view.width, view.height);
  if (shot.ground.length) {
    trace(ctx, shot.ground);
    ctx.fillStyle = FLOOR;
    ctx.fill();
    ctx.strokeStyle = FLOOR_EDGE;
    ctx.stroke();
  }
  for (const face of shot.faces) {
    trace(ctx, face.points);
    ctx.globalAlpha = face.opacity;
    ctx.fillStyle = face.fill;
    ctx.strokeStyle = face.fill; // a face outlined in its own fill closes the seams (sceneDrawing)
    ctx.fill();
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  drawDots(ctx, shot.dots ?? []);
  if (shot.lines) drawLines(ctx, shot.lines);
}

/** A scene's people: a ringed dot each, over the map. */
function drawDots(ctx: CanvasRenderingContext2D, dots: readonly { x: number; y: number }[]): void {
  ctx.fillStyle = ACCENT;
  ctx.strokeStyle = GROUND;
  for (const d of dots) {
    ctx.beginPath();
    ctx.arc(d.x, d.y, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}

/** A character's figure: its traced art, filled as the plate fills it, under the matrix that fits it. */
function drawLines(ctx: CanvasRenderingContext2D, lines: FigureLines): void {
  ctx.save();
  ctx.setTransform(...lines.matrix);
  ctx.fillStyle = ACCENT;
  for (const d of lines.paths) ctx.fill(new Path2D(d));
  ctx.restore();
}

const blobOf = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));

const dataUrlOf = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/** The picture at the first quality that fits, WebP or else JPEG. */
async function encode(canvas: HTMLCanvasElement, max: number): Promise<string | null> {
  for (const q of QUALITIES) {
    const webp = await blobOf(canvas, 'image/webp', q);
    const blob = webp?.type === 'image/webp' ? webp : await blobOf(canvas, 'image/jpeg', q);
    if (!blob) return null;
    const url = await dataUrlOf(blob);
    if (url.length <= max) return url;
  }
  return null;
}

/** A canvas of the card's size and its context, or null where there is none (jsdom). */
function canvasFor(view: Viewport): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = view.width;
  canvas.height = view.height;
  try {
    const ctx = canvas.getContext('2d');
    return ctx ? { canvas, ctx } : null;
  } catch {
    // jsdom throws "not implemented" here: no canvas, no picture - the card keeps its drawing.
    return null;
  }
}

export async function drawThumb(shot: ThumbShot, view: Viewport, max: number): Promise<string | null> {
  const drawn = canvasFor(view);
  if (!drawn) return null;
  paint(drawn.ctx, shot, view);
  return encode(drawn.canvas, max);
}

/**
 * An image's card: the picture scaled to cover the card and centred, on the
 * ground (a transparent PNG shows the ground through). Null where the
 * browser cannot decode it or there is no canvas.
 */
export async function drawImageThumb(blob: Blob, view: Viewport, max: number): Promise<string | null> {
  const drawn = canvasFor(view);
  if (!drawn || typeof createImageBitmap !== 'function') return null;
  const { canvas, ctx } = drawn;
  const bmp = await createImageBitmap(blob);
  try {
    const k = Math.max(view.width / bmp.width, view.height / bmp.height);
    const w = bmp.width * k;
    const h = bmp.height * k;
    ctx.fillStyle = GROUND;
    ctx.fillRect(0, 0, view.width, view.height);
    ctx.drawImage(bmp, (view.width - w) / 2, (view.height - h) / 2, w, h);
  } finally {
    bmp.close();
  }
  return encode(canvas, max);
}
